import { useCallback, useEffect, useRef, useState } from 'react'
import { FolderOpen, Globe, Upload, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/overlay'
import { cn } from '@/lib/cn'
import { formatBytes } from '@/lib/format'
import { RetentionPicker } from './RetentionPicker'
import { useUploads } from './UploadProvider'

/**
 * Staging step before anything is queued.
 *
 * Retention lives here rather than on the toolbar because it applies per
 * upload, is irreversible, and needs to be a deliberate choice made while
 * looking at the specific files it will apply to.
 */
export function UploadDialog({
  bucket,
  prefix,
  initialFiles,
  onClose,
}: {
  bucket: string
  prefix: string
  initialFiles: File[]
  onClose: () => void
}) {
  const { addFiles } = useUploads()
  const [files, setFiles] = useState<File[]>(initialFiles)
  const [retention, setRetention] = useState('')
  const [makePublic, setMakePublic] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)

  // Files dropped onto the page while the dialog was closed arrive here.
  useEffect(() => {
    if (initialFiles.length > 0) setFiles((current) => mergeFiles(current, initialFiles))
  }, [initialFiles])

  const add = useCallback((incoming: FileList | File[] | null) => {
    if (!incoming) return
    setFiles((current) => mergeFiles(current, Array.from(incoming)))
  }, [])

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0)

  async function start() {
    if (files.length === 0) return
    setBusy(true)
    await addFiles(files, bucket, prefix, retention, makePublic)
    setBusy(false)
    onClose()
  }

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title="Upload files"
      description={
        <>
          Destination: <span className="font-mono text-xs">{bucket}/{prefix || ''}</span>
        </>
      }
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={start} loading={busy} disabled={files.length === 0}>
            <Upload className="h-4 w-4" />
            {files.length === 0
              ? 'Upload'
              : `Upload ${files.length} file${files.length === 1 ? '' : 's'} · ${formatBytes(totalBytes)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Drop zone */}
        <div
          onDragEnter={(e) => {
            e.preventDefault()
            dragDepth.current += 1
            setDragging(true)
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => {
            e.preventDefault()
            dragDepth.current -= 1
            if (dragDepth.current <= 0) setDragging(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            dragDepth.current = 0
            setDragging(false)
            add(e.dataTransfer.files)
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
          }}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors',
            dragging ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted/50',
          )}
        >
          <FolderOpen className={cn('h-7 w-7', dragging ? 'text-accent' : 'text-muted-foreground')} />
          <p className="text-sm font-medium">
            {dragging ? 'Drop to add' : 'Drag files here, or click to browse'}
          </p>
          <p className="text-xs text-muted-foreground">
            Files over 8 MB are uploaded in parts and can be resumed
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              add(e.target.files)
              // Reset so picking the same file twice still fires a change.
              e.target.value = ''
            }}
          />
        </div>

        {/* Retention */}
        <div className="space-y-2 rounded-md border border-border bg-surface p-4">
          <RetentionPicker value={retention} onChange={setRetention} />
          {retention !== '' && (
            // The picker already states the duration; this adds the part people
            // tend to assume is escapable.
            <p className="text-xs text-muted-foreground">
              Enforced by HCP itself — no role, setting or administrator action in this app
              can delete them sooner.
            </p>
          )}
        </div>

        {/* Public link */}
        <div className="space-y-2 rounded-md border border-border bg-surface p-4">
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={makePublic}
              onChange={(e) => setMakePublic(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--accent)]"
            />
            <span className="space-y-1">
              <span
                className={cn(
                  'flex items-center gap-1.5 text-sm',
                  makePublic ? 'font-medium text-warning' : '',
                )}
              >
                {makePublic && <Globe className="h-4 w-4" />}
                Make these files publicly downloadable
              </span>
              <span className="block text-xs text-muted-foreground">
                Sets each object's ACL to public-read and shows you a permanent link when the
                upload finishes.
              </span>
            </span>
          </label>

          {makePublic && (
            <p className="text-xs text-warning">
              Anyone with the link can download the file, with no login and no expiry. Downloads
              go straight from HCP, so they are not recorded anywhere — you will not be able to
              see who fetched it. You can revoke a link later from the file's detail panel.
            </p>
          )}
        </div>

        {/* Staged files */}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {files.length} file{files.length === 1 ? '' : 's'} ready
              </p>
              <Button size="sm" variant="ghost" onClick={() => setFiles([])}>
                Clear all
              </Button>
            </div>

            <ul className="max-h-52 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate text-sm" title={file.name}>
                    {file.name}
                  </span>
                  <span className="shrink-0 text-xs tabular text-muted-foreground">
                    {formatBytes(file.size)}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 shrink-0"
                    onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  )
}

/** Appends files, skipping ones already staged. */
function mergeFiles(current: File[], incoming: File[]): File[] {
  const seen = new Set(current.map((f) => `${f.name}|${f.size}|${f.lastModified}`))
  const additions = incoming.filter((f) => !seen.has(`${f.name}|${f.size}|${f.lastModified}`))
  return additions.length > 0 ? [...current, ...additions] : current
}
